import React from 'react';
import moment from 'moment-timezone';
import styled from 'styled-components';
import type { Moment } from 'moment';
import { BodyText, BoldBody } from '@grnhse/seedling/lib/azalea/typography';
import { colors } from '@grnhse/seedling/lib/azalea/constants';
import t from 'shared/utils/translation';
import type { TimeZoneOption, Slot, SlotsByDay } from '../../types';
import { useBonsai } from '@grnhse/bonsai/lib/hooks';
import { Body } from '@grnhse/bonsai/lib/components/typography';
import { ButtonShape } from '@grnhse/bonsai/lib/core/configuration';
import { BORDER_RADIUS } from '@grnhse/bonsai/lib/components/buttons/shared';

type Props = {
  selectedDate: Moment | null | undefined;
  availableSlotsByDay: SlotsByDay;
  selectedTime: Slot | null | undefined;
  selectedTimeZoneOption: TimeZoneOption;
  selectTime: (arg1: Slot) => void;
  bonsaiEnabled: boolean;
};

export default function SlotPicker(props: Props) {
  const {
    selectedDate,
    selectedTime,
    availableSlotsByDay,
    selectedTimeZoneOption,
    selectTime,
    bonsaiEnabled,
  } = props;
  const formattedSelectedDate = moment(selectedDate).format('YYYY-MM-DD');
  const selectedTimeZone = selectedTimeZoneOption.ianaTimeZoneId;
  const bonsaiConfiguration = useBonsai();

  const selectedColor = bonsaiEnabled
    ? bonsaiConfiguration.buttonColor
    : colors.evergreen;

  const selectedTextColor = bonsaiEnabled
    ? bonsaiConfiguration.buttonColor
    : colors.white;

  const fontFamily = bonsaiEnabled
    ? bonsaiConfiguration.secondaryFont
    : 'inherit';

  return (
    <SlotPickerContainer>
      {selectedDate && (
        <SelectedDateWrapper>
          {bonsaiEnabled ? (
            <Body medium>{selectedDate.format('dddd, MMMM Do ')}</Body>
          ) : (
            <BoldBody>{selectedDate.format('dddd, MMMM Do ')}</BoldBody>
          )}
        </SelectedDateWrapper>
      )}
      <Wrapper
        fontFamily={fontFamily}
        selectedColor={selectedColor}
        selectedTextColor={selectedTextColor}
        noDateSelected={!selectedDate}
        bonsai={bonsaiEnabled}
        shape={bonsaiConfiguration.buttonShape}
      >
        {availableSlotsByDay[formattedSelectedDate] ? (
          <ul>
            {availableSlotsByDay[formattedSelectedDate].map((slot) => {
              return (
                <li
                  data-provides="slot"
                  data-provides-for={slot.start}
                  className={selectedTime === slot ? 'selected' : ''}
                  key={slot.start}
                  onClick={() => {
                    selectTime(slot);
                  }}
                >
                  {moment(slot.start)
                    .tz(selectedTimeZone)
                    .format('h:mm a (zz)')}
                </li>
              );
            })}
          </ul>
        ) : (
          <NoneSelected>
            {t('self_schedule.candidate_view.no_time_selected')}
          </NoneSelected>
        )}
      </Wrapper>
    </SlotPickerContainer>
  );
}

const NoneSelected = styled(BodyText)`
  color: ${colors.darkGrey};
  text-align: left;
  margin: 52px 24px;
`;

const SlotPickerContainer = styled.div`
  display: flex;
  flex-direction: column;
  text-align: center;

  @media only screen and (max-width: 480px) {
    margin: 8px 25px;
  }
`;

const SelectedDateWrapper = styled.div`
  margin-bottom: 20px;
  margin-top: 25px;

  @media only screen and (max-width: 480px) {
    margin-top: 16px;
    align-self: flex-start;
  }
`;

const Wrapper = styled.div<{
  fontFamily: string;
  noDateSelected: boolean;
  selectedColor: string;
  selectedTextColor: string;
  bonsai: boolean;
  shape: ButtonShape;
}>`
  font-family: ${(props) => props.fontFamily};
  background: ${(props) => props.noDateSelected && colors.lightestGrey};
  width: 230px;
  height: 350px;
  text-align: center;
  overflow-y: scroll;
  overflow-x: hidden;
  margin-left: 90px;
  margin-top: ${(props) => props.noDateSelected && '35px'};

  &::-webkit-scrollbar {
    width: 6px;
    ${(props) => props.noDateSelected && 'display: none;'}
  }

  &::-webkit-scrollbar-thumb {
    background: ${colors.lightGrey};
    border-radius: 10px;
  }

  ul {
    margin: 52px auto 0;
    ${(props) => !props.noDateSelected && 'margin-top: 0;'}
    width: 140px;
    list-style: none;
    padding: 0 90px 0 0;

    @media only screen and (max-width: 480px) {
      width: 100%;
      padding: 0;
      margin: 0;
    }
  }

  li {
    padding: 10px;
    margin-bottom: 10px;
    cursor: pointer;
    box-sizing: border-box;
    border-radius: ${({ bonsai, shape }) =>
      bonsai ? BORDER_RADIUS[shape] : '50px'};

    &:hover {
      background: ${colors.lighterGrey};
    }

    &.selected {
      color: ${({ selectedTextColor }) => selectedTextColor};
      background: ${({ bonsai, selectedColor }) =>
        bonsai ? 'transparent' : selectedColor};

      ${({ bonsai, selectedColor }) =>
        bonsai && `border: 1px solid ${selectedColor};`}
    }
  }

  @media only screen and (max-width: 480px) {
    height: 300px;
    margin-bottom: 16px;
    margin-left: 0;
    width: 100%;
  }
`;
